import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HERO_IMAGE, LOGO_URL } from "@/constants/site";
import { resolveSiteAsset } from "@/lib/useSiteContent";
import { EASE } from "@/lib/motion";
import { useTranslation } from "react-i18next";

const AUTO_ADVANCE_DELAY = 1400;

export const OpeningHero = ({ content = {} }) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const hasInteracted = useRef(false);
  const settle = { opacity: 1, y: 0 };

  useEffect(() => {
    if (reduceMotion) return undefined;

    const cancelAutoAdvance = () => {
      hasInteracted.current = true;
    };

    const timer = window.setTimeout(() => {
      if (!hasInteracted.current && window.scrollY < 24) {
        window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
      }
    }, AUTO_ADVANCE_DELAY);

    window.addEventListener("wheel", cancelAutoAdvance, { passive: true });
    window.addEventListener("touchstart", cancelAutoAdvance, { passive: true });
    window.addEventListener("keydown", cancelAutoAdvance);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("wheel", cancelAutoAdvance);
      window.removeEventListener("touchstart", cancelAutoAdvance);
      window.removeEventListener("keydown", cancelAutoAdvance);
    };
  }, [reduceMotion]);

  return (
    <section
      id="intro" data-testid="opening-hero"
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden bg-ink px-6 text-center text-cream"
    >
      <motion.img
        aria-hidden="true"
        src={resolveSiteAsset(content.hero_image || HERO_IMAGE.url)}
        alt=""
        initial={reduceMotion ? false : { opacity: 0, scale: 1.03 }}
        animate={{ opacity: 0.2, scale: 1 }}
        transition={{ duration: 1.2, ease: EASE }}
        className="absolute inset-0 h-full w-full object-cover opacity-20 saturate-0"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-ink/65" />
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[46%] bg-gradient-to-t from-brand/20 via-brand/5 to-transparent" />
      <div aria-hidden="true" className="absolute inset-x-6 top-7 border-t border-cream/15 sm:inset-x-12 sm:top-10" />
      <div aria-hidden="true" className="absolute inset-x-6 bottom-7 border-t border-cream/15 sm:inset-x-12 sm:bottom-10" />

      <div className="relative z-10 flex max-w-2xl flex-col items-center">
        <motion.p
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={settle}
          transition={{ duration: 0.65, ease: EASE }}
          className="text-[10px] font-semibold uppercase tracking-[0.34em] text-cream/55 sm:text-xs"
        >
          Nolena Global
        </motion.p>

        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 22 }}
          animate={settle}
          transition={{ duration: 0.8, delay: reduceMotion ? 0 : 0.12, ease: EASE }}
          className="mt-7 border-y border-cream/20 px-4 py-7 sm:mt-9 sm:px-8 sm:py-9"
        >
          <img src={LOGO_URL} alt="Nolena Global" className="h-20 w-auto brightness-0 invert sm:h-28" />
        </motion.div>

        <motion.h1
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          animate={settle}
          transition={{ duration: 0.7, delay: reduceMotion ? 0 : 0.28, ease: EASE }}
          className="mt-8 font-heading text-3xl font-semibold tracking-tight text-cream sm:mt-10 sm:text-5xl"
        >
          {content.opening_title || "Pet ürünlerinde güvenilir üretim."}
        </motion.h1>
      </div>

      <motion.div
        initial={reduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: reduceMotion ? 0 : 0.7 }}
        className="absolute bottom-12 flex flex-col items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.26em] text-cream/55 sm:bottom-16"
      >
        <span>{t("common.explore")}</span>
        <motion.span
          aria-hidden="true"
          animate={reduceMotion ? { y: 0 } : { y: [0, 7, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-brand"
        />
      </motion.div>
    </section>
  );
};
